import { TrainingEvent } from '@alucio/aws-beacon-amplify/src/models'
import { generateClient } from 'aws-amplify/api';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
  commonReducers,
  initialState,
  SliceStatus,
} from './common'
import { listTrainingEvents } from '@alucio/aws-beacon-amplify/src/graphql/queries';
import { createTrainingEvent, updateTrainingEvent } from '@alucio/aws-beacon-amplify/src/graphql/mutations';
import * as logger from 'src/utils/logger';
import { OmitDeep } from '@alucio/awshelpers/src/utils';

const sliceName = 'trainingEvent'
const { reducers } = commonReducers<TrainingEvent>(sliceName)

const getTrainingEvents = createAsyncThunk<TrainingEvent[], string>(
  'trainingEvent/getTrainingEvents',
  async (tenantId: string, { rejectWithValue }) => {
    try {
      const appsyncClient = generateClient();
      const { data } = await appsyncClient.graphql({
        query: listTrainingEvents,
        variables: { filter: { tenantId: { eq: tenantId } } },
      })

      return data?.listTrainingEvents.items as unknown as TrainingEvent[]
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

const saveTrainingEvent = createAsyncThunk(
  'trainingEvent/saveTrainingEvent',
  async (trainingEventPayload: TrainingEvent) => {
    try {
      const appsyncClient = generateClient()
      const { data } = await appsyncClient.graphql({
        query: createTrainingEvent,
        variables: {
          input: { ...trainingEventPayload },
        },
      })
      return data?.createTrainingEvent
    } catch (err) {
      logger.trainingEvent.error('error in creating training event', err)
      throw new Error('error in creating training event')
    }
  },
)

const editTrainingEvent = createAsyncThunk(
  'trainingEvent/updateTrainingEvent',
  async (trainingEventPayload: TrainingEvent) => {
    const updatedPayload = OmitDeep(
      trainingEventPayload,
      ['_deleted', '_lastChangedAt', '__typename', 'deleted'],
    ) as TrainingEvent & { _version: number };
    try {
      const appsyncClient = generateClient()
      const { data } = await appsyncClient.graphql({
        query: updateTrainingEvent,
        variables: { input: updatedPayload },
      })
      return data.updateTrainingEvent
    } catch (err) {
      logger.trainingEvent.error('error in updating training event', err)
      throw new Error('error in updating training event')
    }
  },
)

const removeTrainingEvent = createAsyncThunk(
  'trainingEvent/deleteTrainingEvent',
  async (trainingEventPayload: TrainingEvent) => {
    const updatedPayload = OmitDeep(
      trainingEventPayload,
      ['_deleted', '_lastChangedAt', '__typename', 'deleted'],
    ) as TrainingEvent & { _version: number };
    try {
      const appsyncClient = generateClient()
      const { data } = await appsyncClient.graphql({
        query: updateTrainingEvent,
        variables: {
          input: {
            id: updatedPayload.id,
            tenantId: updatedPayload.tenantId,
            deleted: true,
            _version: updatedPayload._version,
          },
        },
      })
      return data.updateTrainingEvent
    } catch (err) {
      logger.trainingEvent.error('error in deleting training event', err)
      throw new Error('error in deleting training event')
    }
  },
)

const trainingEventSlice = createSlice({
  name: sliceName,
  initialState: initialState<TrainingEvent>(),
  reducers: {
    ...reducers,
  },
  extraReducers: {
    [getTrainingEvents.fulfilled.toString()]: (state, resp: { payload: TrainingEvent[]}) => {
      state.status = SliceStatus.OK;
      state.hydrated = true;
      state.records = resp.payload;
    },
    [getTrainingEvents.rejected.toString()]: (state) => {
      state.status = SliceStatus.ERROR;
    },
  },
})

export default trainingEventSlice
export const trainingEventActions = {
  ...trainingEventSlice.actions,
  getTrainingEvents,
  saveTrainingEvent,
  editTrainingEvent,
  removeTrainingEvent,
}
